import { QueryKey, useMutation, useQueryClient } from '@tanstack/react-query';
import { CalendarEvent, PredictedSleep } from '@demind-inc/core';
import dayjs from 'dayjs';
import { useRecoilState } from 'recoil';

import { metricsApi } from '../api';
import { eventsSnackBarAtom } from '../recoil';

interface UseUpdateSleepPredictionParams {
  metricId: string;
  date: string;
  prediction: PredictedSleep;
  shouldUpdateEvent?: boolean;
  editedManually?: boolean;
  baseCalendarId?: string;
  chatSessionId?: string;
}

export function useUpdateSleepPrediction() {
  const queryClient = useQueryClient();
  const [_, setEventsSnackbar] = useRecoilState(eventsSnackBarAtom);

  const updateSleepPredictionMutation = useMutation({
    mutationFn: ({
      metricId,
      date,
      prediction,
      shouldUpdateEvent,
      editedManually,
      baseCalendarId,
      chatSessionId,
    }: UseUpdateSleepPredictionParams) => {
      return metricsApi.updateSleepPrediction(metricId, {
        date,
        prediction,
        shouldUpdateEvent,
        editedManually,
        baseCalendarId,
        chatSessionId,
      });
    },
    onMutate: async ({ date, prediction }) => {
      const { sleepTime, wakeTime } = prediction;

      if (!sleepTime || !wakeTime) return {};

      setEventsSnackbar({ message: 'Event updating...', type: 'loading' });

      await queryClient.cancelQueries({ queryKey: ['lifestack.calendar.events'] });

      const queries = queryClient.getQueriesData({ queryKey: ['lifestack.calendar.events'] });
      const previousQueries: [QueryKey, CalendarEvent[]][] = [];

      queries.forEach(([queryKey, data]) => {
        if (data && Array.isArray(data)) {
          previousQueries.push([queryKey, data]);

          const updatedData = data.map((event: CalendarEvent) => {
            const isSleepEvent = event.autoScheduleFrom?.from === 'sleep';
            if (!isSleepEvent) return event;

            if (!dayjs(event.start?.date).isSame(date, 'day')) return event;

            return {
              ...event,
              start: {
                ...event.start,
                date: dayjs(date).format('YYYY-MM-DD') + 'T' + sleepTime + ':00',
              },
              end: {
                ...event.end,
                date: dayjs(date).format('YYYY-MM-DD') + 'T' + wakeTime + ':00',
              },
              description: prediction.description,
            };
          });

          queryClient.setQueryData(queryKey, updatedData);
        }
      });

      return { previousQueries };
    },
    onSuccess: (_, { prediction }) => {
      const { sleepTime, wakeTime } = prediction;

      if (!sleepTime || !wakeTime) return;

      queryClient.invalidateQueries({ queryKey: ['lifestack.calendar.events'] });
      setEventsSnackbar({ message: 'Event updated', type: 'success' });
    },
    onError: (_err, _variables, context) => {
      if (context?.previousQueries) {
        context.previousQueries.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
      }
    },
  });

  return {
    updateSleepPrediction: updateSleepPredictionMutation.mutateAsync,
    ...updateSleepPredictionMutation,
  };
}
